import { z } from 'zod';
import { SessionTypeSchema } from './schedule.js';
/**
 * Per-session attendance — who was actually in the room.
 *
 * A DIFFERENT number from arrival at the front desk (`attendees.checkedInAt`,
 * the `/arrivals` count, the doors Live Activity), and a different number again
 * from the `content_open` telemetry the activity console calls a "view". The
 * vocabulary is deliberate and must not be mixed in any surface that renders
 * these shapes: the front desk says **arrived**, this says **attended**, and
 * telemetry says **viewed**. Two representations of one number drifting apart
 * is the bug `summary_entity.ts` exists to prevent; three labels for three
 * genuinely different numbers is how it stays prevented.
 *
 * Timestamps are ISO strings here rather than dates: this is the wire shape the
 * console parses straight out of `response.json()`.
 */
/**
 * The most rows any single attendance read returns.
 *
 * A ceiling, not a page: the per-session attendee list renders whole, in a
 * drawer and in a bottom sheet, and neither has a "load more". Duplicated from
 * `backend/src/domain/entities/session_attendance_entity.ts` and pinned to it —
 * with a behavioural boundary case on each side — in
 * `backend/tests/contracts.test.ts`.
 */
export const SESSION_ATTENDANCE_ROW_CAP = 2000;
/** What `GET …/attendance/records` returns when the caller asks for nothing. */
export const ATTENDANCE_RECORDS_LIMIT_DEFAULT = 500;
/** The most rows one page of the export feed will ever return. */
export const ATTENDANCE_RECORDS_LIMIT_MAX = 2000;
/** A person, as every attendance surface renders them. */
export const AttendanceAttendeeSchema = z.object({
    id: z.string().uuid(),
    displayName: z.string(),
    company: z.string().nullable(),
    title: z.string().nullable(),
});
/**
 * `POST /v1/sessions/:sessionId/attendance/scan` — the raw badge string, or the
 * code typed off the card when the scanner will not read. Exactly one.
 *
 * Literally the front desk's request schema rather than a copy of it: one card
 * feeds both doors, so a code the building accepts and the room refuses would be
 * a volunteer in a doorway being told the badge in their hand is not real.
 */
export { BadgeCheckInRequestSchema as SessionScanRequestSchema } from './attendees.js';
/**
 * The door's answer.
 *
 * `status` is `'recorded'` only for a genuine FIRST scan; a repeat is
 * `'already'` with the original `scannedAt`, and it is a 200, not an error. A
 * client that cannot tell the difference should count an unknown status as NOT
 * first — under-counting is the safer direction to be wrong in.
 */
export const SessionScanResponseSchema = z.object({
    status: z.enum(['recorded', 'already']),
    scannedAt: z.string(),
    attendee: AttendanceAttendeeSchema,
    session: z.object({ id: z.string().uuid(), title: z.string() }),
    attended: z.number().int().nonnegative(),
});
/** `GET /v1/sessions/:sessionId/attendance` — one room, and who was in it. */
export const SessionAttendanceResponseSchema = z.object({
    session: z.object({
        id: z.string().uuid(),
        title: z.string(),
        startAt: z.string(),
        endAt: z.string(),
        room: z.string().nullable(),
        type: SessionTypeSchema,
        capacity: z.number().int().positive().nullable(),
    }),
    attended: z.number().int().nonnegative(),
    attendees: z.array(AttendanceAttendeeSchema.extend({ scannedAt: z.string() })),
});
export const AttendanceSummaryRowSchema = z.object({
    sessionId: z.string().uuid(),
    title: z.string(),
    type: SessionTypeSchema,
    startAt: z.string(),
    endAt: z.string(),
    room: z.string().nullable(),
    capacity: z.number().int().positive().nullable(),
    attended: z.number().int().nonnegative(),
});
/**
 * `GET /v1/events/:eventId/attendance` — EVERY session, zero-attendance ones
 * included, plus the two header numbers.
 *
 * `uniqueAttendees` is required and SERVED, because it is the one number `data`
 * cannot produce: summing `attended` across the rows double-counts anybody who
 * came to two sessions, so a console rendering that sum under the words "unique
 * attendees" would show a number nothing in the system agrees with.
 *
 * `totalScans` IS that sum, and is served alongside it so the console does not
 * have to decide which arithmetic is safe on which number. It is required for
 * the same reason: an optional key invites a local fallback, and a local
 * fallback is a second representation waiting to drift.
 */
export const AttendanceSummaryResponseSchema = z.object({
    data: z.array(AttendanceSummaryRowSchema),
    uniqueAttendees: z.number().int().nonnegative(),
    totalScans: z.number().int().nonnegative(),
});
/** `GET /v1/events/:eventId/attendance/attendees/:attendeeId` — one person's day. */
export const AttendeeAttendanceResponseSchema = z.object({
    attendee: AttendanceAttendeeSchema,
    sessions: z.array(z.object({
        sessionId: z.string().uuid(),
        title: z.string(),
        startAt: z.string(),
        endAt: z.string(),
        room: z.string().nullable(),
        scannedAt: z.string(),
    })),
});
/**
 * The export feed's query. `limit` is coerced because it arrives as a string in
 * the query string, and capped at the same number the server caps it at — a
 * console that offered a limit the server refuses is the cross-seam drift
 * docs/testing-discipline.md is about.
 */
export const AttendanceRecordsQuerySchema = z.object({
    limit: z.coerce
        .number()
        .int()
        .min(1)
        .max(ATTENDANCE_RECORDS_LIMIT_MAX)
        .default(ATTENDANCE_RECORDS_LIMIT_DEFAULT),
    cursor: z.string().min(1).max(500).optional(),
});
export const AttendanceRecordSchema = z.object({
    sessionId: z.string().uuid(),
    sessionTitle: z.string(),
    startAt: z.string(),
    room: z.string().nullable(),
    attendeeId: z.string().uuid(),
    displayName: z.string(),
    company: z.string().nullable(),
    title: z.string().nullable(),
    scannedAt: z.string(),
});
/**
 * `GET /v1/events/:eventId/attendance/records` — the flat feed a CSV export
 * walks. `meta.nextCursor` is null on the last page, including a last page that
 * happens to be exactly full.
 */
export const AttendanceRecordsResponseSchema = z.object({
    data: z.array(AttendanceRecordSchema),
    meta: z.object({ nextCursor: z.string().nullable() }),
});
//# sourceMappingURL=attendance.js.map